<template>
  <div class="certificate-work-order-list">
    <div class="page-header">
      <h1 class="page-title">
        <i class="fa-solid fa-list"></i>
        보철 인증서 전달 내역
      </h1>
      <div class="page-desc">
        병원에 전달한 보철 인증서 정보를 조회할 수 있습니다.
      </div>
    </div>

    <div class="content-container">
      <!-- 필터 및 검색 영역 -->
      <div class="filter-section">
        <div class="filter-left">
          <div class="total-count">
            전체 <span class="count-number">{{ totalCount }}</span>건
          </div>
        </div>
        <div class="filter-right">
          <a-input-search
            v-model:value="searchKeyword"
            placeholder="병원명, 환자명으로 검색"
            style="width: 300px"
            @search="handleSearch"
            enter-button
          />
          <a-button
            type="primary"
            @click="goToCreate"
            class="create-btn"
          >
            <i class="fa-solid fa-plus"></i>
            새 인증서 정보 전달
          </a-button>
        </div>
      </div>

      <!-- 테이블 -->
      <div class="table-section">
        <a-table
          :columns="columns"
          :data-source="workOrders"
          :loading="loading"
          :pagination="pagination"
          @change="handleTableChange"
          row-key="_id"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'hospital'">
              <div class="hospital-info">
                <div class="hospital-name">{{ record.hospital.name }}</div>
                <div class="hospital-sub">{{ formatBusinessNumber(record.hospital.cNo) }}</div>
              </div>
            </template>
            <template v-else-if="column.key === 'patient'">
              <div class="patient-info">
                <div class="patient-name">{{ record.patient.name }}</div>
                <div class="patient-mobile">{{ record.patient.displayMobile }}</div>
              </div>
            </template>
            <template v-else-if="column.key === 'prosthetic'">
              <div class="prosthetic-info">
                <a-tag v-if="record.prosthetic.prostheticType" color="#FFD814">
                  {{ getProstheticTypeText(record.prosthetic.prostheticType) }}
                </a-tag>
                <!-- toothDetails의 treatmentType도 표시 -->
                <template v-if="record.prosthetic.toothDetails?.length">
                  <a-tag
                    v-for="(detail, idx) in getUniqueTreatmentTypes(record.prosthetic.toothDetails)"
                    :key="idx"
                    color="#FFD814"
                    style="font-size: 12px;"
                  >
                    {{ getTreatmentTypeText(detail) }}
                  </a-tag>
                </template>
                <span class="tooth-count">
                  {{ record.prosthetic.selectedTeeth.length }}개 치아
                </span>
              </div>
            </template>
            <template v-else-if="column.key === 'status'">
              <a-tag :color="getStatusColor(record.status)">
                {{ getStatusText(record.status) }}
              </a-tag>
            </template>
            <template v-else-if="column.key === 'createdAt'">
              {{ formatDate(record.createdAt) }}
            </template>
            <template v-else-if="column.key === 'action'">
              <a-button type="link" @click="viewDetail(record)">
                상세보기
              </a-button>
            </template>
          </template>

          <template #emptyText>
            <div class="empty-state">
              <i class="fa-solid fa-inbox"></i>
              <p>전달 내역이 없습니다.</p>
              <a-button type="primary" @click="goToCreate">
                첫 인증서 정보 전달하기
              </a-button>
            </div>
          </template>
        </a-table>
      </div>
    </div>

    <!-- 상세 드로워 -->
    <a-drawer
      v-model:open="detailModalVisible"
      :width="1000"
      placement="right"
      :destroy-on-close="true"
      class="detail-drawer"
    >
      <template #title>
        <div class="drawer-title-wrapper">
          <i class="fa-solid fa-clipboard-list"></i>
          <span>보철 인증서 전달 상세</span>
          <a-tag :color="getStatusColor(selectedWorkOrder?.status)" v-if="selectedWorkOrder" style="margin-left: 12px;">
            {{ getStatusText(selectedWorkOrder?.status) }}
          </a-tag>
        </div>
      </template>
      <template #extra>
        <a-space>
          <a-button type="text" @click="detailModalVisible = false">
            <i class="fa-solid fa-times"></i>
          </a-button>
        </a-space>
      </template>
      <div v-if="selectedWorkOrder" class="detail-drawer-content">
        <!-- 병원 정보 -->
        <a-card class="detail-card" :bordered="false">
          <template #title>
            <div class="card-title">
              <a-avatar :style="{ backgroundColor: '#FFD814' }" shape="square">
                <template #icon><i class="fa-solid fa-hospital"></i></template>
              </a-avatar>
              <span style="margin-left: 8px;">병원 정보</span>
            </div>
          </template>
          <a-descriptions :column="2" :label-style="{ width: '120px', fontWeight: 500 }">
            <a-descriptions-item label="병원명">
              <span class="info-value">{{ selectedWorkOrder.hospital.name }}</span>
            </a-descriptions-item>
            <a-descriptions-item label="사업자번호">
              <span class="info-value">{{ formatBusinessNumber(selectedWorkOrder.hospital.cNo) }}</span>
            </a-descriptions-item>
            <a-descriptions-item label="대표자">
              <span class="info-value">{{ selectedWorkOrder.hospital.ceo || '-' }}</span>
            </a-descriptions-item>
            <a-descriptions-item label="연락처">
              <span class="info-value">{{ selectedWorkOrder.hospital.call || '-' }}</span>
            </a-descriptions-item>
          </a-descriptions>
        </a-card>

        <!-- 환자 정보 -->
        <a-card class="detail-card" :bordered="false">
          <template #title>
            <div class="card-title">
              <a-avatar :style="{ backgroundColor: '#FFD814' }" shape="square">
                <template #icon><i class="fa-solid fa-user"></i></template>
              </a-avatar>
              <span style="margin-left: 8px;">환자 정보</span>
            </div>
          </template>
          <a-descriptions :column="2" :label-style="{ width: '120px', fontWeight: 500 }">
            <a-descriptions-item label="환자명">
              <span class="info-value">{{ selectedWorkOrder.patient.name }}</span>
            </a-descriptions-item>
            <a-descriptions-item label="연락처">
              <span class="info-value">{{ selectedWorkOrder.patient.displayMobile }}</span>
            </a-descriptions-item>
            <a-descriptions-item label="차트번호">
              <span class="info-value">{{ selectedWorkOrder.chartNumber || '-' }}</span>
            </a-descriptions-item>
            <a-descriptions-item label="이메일">
              <span class="info-value">{{ selectedWorkOrder.patient.email || '-' }}</span>
            </a-descriptions-item>
            <a-descriptions-item label="성별">
              <span class="info-value">{{ getGenderText(selectedWorkOrder.patient.gender) }}</span>
            </a-descriptions-item>
            <a-descriptions-item label="생년월일">
              <span class="info-value">{{ selectedWorkOrder.patient.birthDate || '-' }}</span>
            </a-descriptions-item>
            <a-descriptions-item label="시술일">
              <span class="info-value">{{ selectedWorkOrder.patient.surgeryDate || '-' }}</span>
            </a-descriptions-item>
          </a-descriptions>
        </a-card>

        <!-- 보철 정보 -->
        <a-card class="detail-card" :bordered="false">
          <template #title>
            <div class="card-title">
              <a-avatar :style="{ backgroundColor: '#FFD814' }" shape="square">
                <template #icon><i class="fa-solid fa-tooth"></i></template>
              </a-avatar>
              <span style="margin-left: 8px;">보철 정보</span>
            </div>
          </template>
          <a-descriptions :column="1" :label-style="{ width: '120px', fontWeight: 500 }">
            <a-descriptions-item v-if="selectedWorkOrder.prosthetic.prostheticType" label="보철 종류">
              <a-tag color="#FFD814" style="font-size: 14px; padding: 4px 12px;">
                {{ getProstheticTypeText(selectedWorkOrder.prosthetic.prostheticType) }}
              </a-tag>
            </a-descriptions-item>
            <a-descriptions-item label="선택 치아">
              <div class="teeth-badges">
                <a-badge
                  v-for="tooth in selectedWorkOrder.prosthetic.selectedTeeth.sort((a, b) => a - b)"
                  :key="tooth"
                  :count="tooth"
                  :number-style="{ backgroundColor: '#52c41a' }"
                  style="margin-right: 8px;"
                />
              </div>
            </a-descriptions-item>
          </a-descriptions>

          <!-- 치아별 상세 -->
          <div v-if="selectedWorkOrder.prosthetic.toothDetails?.length" class="tooth-details-section">
            <a-divider orientation="left">
              <span style="font-size: 13px; color: #666;">
                <i class="fa-solid fa-list" style="color: #FFD814; margin-right: 8px;"></i>치아별 상세 정보
              </span>
            </a-divider>
            <a-list
              :data-source="selectedWorkOrder.prosthetic.toothDetails"
              :grid="{ gutter: 16, xs: 1, sm: 2, md: 2, lg: 3, xl: 3, xxl: 3 }"
            >
              <template #renderItem="{ item }">
                <a-list-item style="padding: 0;">
                  <a-card size="small" :hoverable="true">
                    <div class="tooth-card">
                      <div class="tooth-header">
                        <i class="fa-solid fa-tooth" style="color: #FFD814; margin-right: 8px;"></i>
                        <span class="tooth-number">{{ formatToothNumbers(item.toothNumbers) }}</span>
                        <a-tag v-if="item.treatmentType" color="#FFD814" size="small" style="margin-left: 8px;">
                          {{ getTreatmentTypeText(item.treatmentType) }}
                        </a-tag>
                        <a-tag v-if="item.stm" color="#FFD814" size="small" style="margin-left: 8px;">STM</a-tag>
                      </div>
                      <div class="tooth-product">{{ item.product }}</div>
                      <div v-if="item.gtinCode" class="tooth-gtin">
                        <i class="fa-solid fa-barcode" style="color: #999; margin-right: 6px;"></i>
                        <span>GTIN: {{ item.gtinCode }}</span>
                      </div>
                    </div>
                  </a-card>
                </a-list-item>
              </template>
            </a-list>
          </div>
        </a-card>

        <!-- 약관 동의 -->
        <a-card class="detail-card" :bordered="false">
          <template #title>
            <div class="card-title">
              <a-avatar :style="{ backgroundColor: '#FFD814' }" shape="square">
                <template #icon><i class="fa-solid fa-check-square"></i></template>
              </a-avatar>
              <span style="margin-left: 8px;">약관 동의</span>
            </div>
          </template>
          <a-list :data-source="agreementItems" :split="false">
            <template #renderItem="{ item }">
              <a-list-item style="padding: 8px 0;">
                <a-list-item-meta>
                  <template #avatar>
                    <a-avatar
                      :style="{
                        backgroundColor: item.agreed ? '#52c41a' : '#ff4d4f',
                        fontSize: '16px'
                      }"
                      size="small"
                    >
                      <template #icon>
                        <i :class="['fa-solid', item.agreed ? 'fa-check' : 'fa-times']"></i>
                      </template>
                    </a-avatar>
                  </template>
                  <template #title>
                    <span style="font-size: 14px; color: #333;">{{ item.title }}</span>
                  </template>
                </a-list-item-meta>
              </a-list-item>
            </template>
          </a-list>
        </a-card>

        <!-- 전달 정보 -->
        <a-card class="detail-card" :bordered="false">
          <template #title>
            <div class="card-title">
              <a-avatar :style="{ backgroundColor: '#FFD814' }" shape="square">
                <template #icon><i class="fa-solid fa-info-circle"></i></template>
              </a-avatar>
              <span style="margin-left: 8px;">전달 정보</span>
            </div>
          </template>
          <a-descriptions :column="2" :label-style="{ width: '120px', fontWeight: 500 }">
            <a-descriptions-item label="상태">
              <a-tag :color="getStatusColor(selectedWorkOrder.status)" style="font-size: 14px;">
                {{ getStatusText(selectedWorkOrder.status) }}
              </a-tag>
            </a-descriptions-item>
            <a-descriptions-item label="생성일시">
              <span class="info-value">
                <i class="fa-solid fa-clock" style="margin-right: 8px; color: #FFD814;"></i>
                {{ formatDateTime(selectedWorkOrder.createdAt) }}
              </span>
            </a-descriptions-item>
            <a-descriptions-item label="작업일자">
              <span class="info-value">
                <i class="fa-solid fa-calendar-check" style="margin-right: 8px; color: #FFD814;"></i>
                {{ selectedWorkOrder.worker?.workDate || '-' }}
              </span>
            </a-descriptions-item>
            <a-descriptions-item label="보철 납품일">
              <span class="info-value">
                <i class="fa-solid fa-truck" style="margin-right: 8px; color: #FFD814;"></i>
                {{ selectedWorkOrder.prostheticDeliveryDate || '-' }}
              </span>
            </a-descriptions-item>
          </a-descriptions>
        </a-card>
      </div>
    </a-drawer>
  </div>
</template>

<script>
import { getAccessToken } from '@/utils/tokenStore';
import workOrderApi from '@/apis/workOrder';
import apiInstance from '@/axios';
import { businessNumberFormatter, jwtDecoder } from '@/utils/utils';

export default {
  name: 'CertificateWorkOrderList',
  data() {
    return {
      loading: false,
      workOrders: [],
      totalCount: 0,
      searchKeyword: '',
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showTotal: (total) => `총 ${total}건`
      },
      columns: [
        {
          title: '병원',
          key: 'hospital',
          width: 200,
          ellipsis: true
        },
        {
          title: '환자',
          key: 'patient',
          width: 150
        },
        {
          title: '보철 정보',
          key: 'prosthetic',
          width: 180
        },
        {
          title: '상태',
          key: 'status',
          width: 100,
          align: 'center'
        },
        {
          title: '생성일',
          key: 'createdAt',
          width: 120,
          align: 'center'
        },
        {
          title: '관리',
          key: 'action',
          width: 100,
          align: 'center',
          fixed: 'right'
        }
      ],
      detailModalVisible: false,
      selectedWorkOrder: null,
      agreementItems: []
    };
  },
  mounted() {
    this.fetchWorkOrders();
  },
  methods: {
    async fetchWorkOrders() {
      this.loading = true;
      try {
        const params = {
          page: this.pagination.current,
          limit: this.pagination.pageSize
        };

        if (this.searchKeyword) {
          params.search = this.searchKeyword;
        }

        // 본인이 생성한 work-order만 조회하도록 dentalLaboratoryId 필터링 추가
        const dentalLaboratoryId = jwtDecoder(getAccessToken());
        if (dentalLaboratoryId) {
          params.dentalLaboratoryId = dentalLaboratoryId;
        }

        const api = workOrderApi(apiInstance, this.$store);
        await api.getWorkOrders(
          params,
          (response) => {
            this.workOrders = response.data.data || [];
            this.totalCount = response.data.total || 0;
            this.pagination.total = response.data.total || 0;
          },
          (error) => {
            console.error('목록 조회 실패:', error);
            this.$message.error('목록을 불러오는데 실패했습니다.');
          }
        );
      } finally {
        this.loading = false;
      }
    },

    handleSearch() {
      this.pagination.current = 1;
      this.fetchWorkOrders();
    },

    handleTableChange(pagination) {
      this.pagination.current = pagination.current;
      this.pagination.pageSize = pagination.pageSize;
      this.fetchWorkOrders();
    },

    async viewDetail(record) {
      this.loading = true;
      try {
        const api = workOrderApi(apiInstance, this.$store);
        await api.getWorkOrderDetail(
          record._id,
          (response) => {
            this.selectedWorkOrder = response.data.data;
            // 약관 동의 데이터 구성
            this.agreementItems = [
              {
                title: '개인정보 수집 및 이용 동의',
                agreed: this.selectedWorkOrder.agreements.privacy
              },
              {
                title: '서비스 이용 약관 동의',
                agreed: this.selectedWorkOrder.agreements.service
              },
              {
                title: '마케팅 정보 수신 동의',
                agreed: this.selectedWorkOrder.agreements.marketing
              }
            ];
            this.detailModalVisible = true;
          },
          (error) => {
            console.error('상세 조회 실패:', error);
            this.$message.error('상세 정보를 불러오는데 실패했습니다.');
          }
        );
      } finally {
        this.loading = false;
      }
    },

    goToCreate() {
      this.$router.push('/certificate-work-order');
    },

    formatBusinessNumber(value) {
      return businessNumberFormatter(value);
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    },

    formatDateTime(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return `${this.formatDate(dateString)} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    },

    getProstheticTypeText(type) {
      const typeMap = {
        'crown': '크라운',
        'implant-crown': '임플란트 크라운',
        'implant_crown': '임플란트 크라운',
        'bridge': '브릿지',
        'inlay': '인레이',
        'laminate': '라미네이트'
      };
      return typeMap[type?.toLowerCase()] || type;
    },

    getTreatmentTypeText(type) {
      const typeMap = {
        'CROWN': '크라운',
        'IMPLANT_CROWN': '임플란트 크라운',
        'BRIDGE': '브릿지',
        'INLAY': '인레이',
        'LAMINATE': '라미네이트',
        'ONLAY': '온레이',
        'VENEER': '베니어'
      };
      return typeMap[type?.toUpperCase()] || type;
    },

    getUniqueTreatmentTypes(toothDetails) {
      if (!toothDetails || !Array.isArray(toothDetails)) return [];
      const types = toothDetails
        .map(detail => detail.treatmentType)
        .filter(type => type);
      return [...new Set(types)];
    },

    formatToothNumbers(toothNumbers) {
      if (!toothNumbers || !Array.isArray(toothNumbers) || toothNumbers.length === 0) {
        return '-';
      }
      const sorted = [...toothNumbers].sort((a, b) => a - b);
      return sorted.join(', ') + '번';
    },

    getGenderText(gender) {
      const genderMap = {
        'male': '남성',
        'female': '여성',
        'unknown': '모름'
      };
      return genderMap[gender] || '-';
    },

    getStatusText(status) {
      const statusMap = {
        'pending': '대기',
        'sent': '전송완료',
        'confirmed': '확인완료',
        'completed': '인증서 발행됨',
        'failed': '실패'
      };
      return statusMap[status] || status;
    },

    getStatusColor(status) {
      const colorMap = {
        'pending': 'orange',
        'sent': 'blue',
        'confirmed': 'green',
        'completed': 'success',
        'failed': 'red'
      };
      return colorMap[status] || 'default';
    }
  }
};
</script>

<style scoped lang="scss">
.certificate-work-order-list {
  min-height: 100vh;
  background: #f5f7fa;
  padding: 30px;

  .page-header {
    max-width: 1440px;
    margin: 0 auto 30px;
    background: white;
    padding: 30px 40px;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    display: block !important;
    flex-direction: column !important;
    align-items: flex-start !important;

    .page-title {
      font-size: 28px;
      font-weight: 900;
      color: #2F3337;
      margin: 0 0 12px 0;
      letter-spacing: -0.5px;
      display: block;

      i {
        color: #FFD814;
        font-size: 24px;
        margin-right: 12px;
      }
    }

    .page-desc {
      font-size: 14px;
      color: #6e747c;
      line-height: 1.6;
    }
  }

  .content-container {
    max-width: 1440px;
    margin: 0 auto;
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    padding: 30px;

    .filter-section {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;

      .filter-left {
        .total-count {
          font-size: 16px;
          color: #333;

          .count-number {
            font-weight: 700;
            color: #FFD814;
            font-size: 18px;
            margin: 0 4px;
          }
        }
      }

      .filter-right {
        display: flex;
        gap: 12px;

        .create-btn {
          background: #FFD814;
          border: none;
          color: #333;
          font-weight: 500;

          &:hover {
            background: #FFC700;
          }

          i {
            margin-right: 4px;
          }
        }
      }
    }

    .table-section {
      .hospital-info {
        .hospital-name {
          font-weight: 500;
          color: #333;
        }

        .hospital-sub {
          font-size: 12px;
          color: #999;
          margin-top: 2px;
        }
      }

      .patient-info {
        .patient-name {
          font-weight: 500;
          color: #333;
        }

        .patient-mobile {
          font-size: 12px;
          color: #999;
          margin-top: 2px;
        }
      }

      .prosthetic-info {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;

        .tooth-count {
          font-size: 13px;
          color: #666;
        }
      }

      .empty-state {
        padding: 60px 0;
        text-align: center;

        i {
          font-size: 48px;
          color: #d9d9d9;
          margin-bottom: 16px;
        }

        p {
          font-size: 16px;
          color: #999;
          margin-bottom: 24px;
        }
      }
    }
  }

  // 상세 드로워 스타일
  .detail-drawer {
    ::v-deep(.ant-drawer-header) {
      background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
      padding: 20px 24px;
      border-bottom: 2px solid #FFD814;

      .drawer-title-wrapper {
        display: flex;
        align-items: center;
        gap: 12px;
        font-size: 18px;
        font-weight: 700;
        color: #2F3337;

        i {
          color: #FFD814;
          font-size: 20px;
        }
      }
    }

    ::v-deep(.ant-drawer-body) {
      padding: 20px;
      background: linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%);
    }

    ::v-deep(.ant-drawer-extra) {
      .ant-btn {
        color: #666;
        &:hover {
          color: #FFD814;
        }
      }
    }
  }

  .detail-drawer-content {
    .detail-card {
      margin-bottom: 20px;
      border-radius: 12px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      transition: all 0.3s ease;

      &:hover {
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
        transform: translateY(-2px);
      }

      .card-title {
        display: flex;
        align-items: center;
        font-size: 16px;
        font-weight: 600;
        color: #2F3337;
      }

      ::v-deep(.ant-card-head) {
        background: linear-gradient(to right, #fafafa 0%, #ffffff 100%);
        border-bottom: 1px solid #f0f0f0;
        padding: 16px 24px;
      }

      ::v-deep(.ant-card-body) {
        padding: 20px 24px;
      }

      .info-value {
        font-size: 14px;
        color: #333;
        font-weight: 500;
      }

      .teeth-badges {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
      }

      .tooth-details-section {
        margin-top: 20px;

        .tooth-card {
          .tooth-header {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 6px;
            margin-bottom: 8px;
            color: #2F3337;
            font-weight: 600;

            .tooth-number {
              font-size: 15px;
              font-weight: 700;
            }
          }

          .tooth-product {
            font-size: 13px;
            color: #666;
            padding-left: 24px;
          }

          .tooth-gtin {
            font-size: 12px;
            color: #999;
            padding-left: 24px;
            margin-top: 4px;
            display: flex;
            align-items: center;
          }
        }
      }
    }

    // Ant Design Vue 컴포넌트 커스터마이징
    ::v-deep(.ant-descriptions-item-label) {
      background: #f8f9fa;
      padding: 8px 12px;
      border-radius: 4px;
    }

    ::v-deep(.ant-descriptions-item-content) {
      padding: 8px 12px;
    }

    ::v-deep(.ant-list-item) {
      border-radius: 8px;
      transition: background 0.3s ease;

      &:hover {
        background: #f8f9fa;
      }
    }

    ::v-deep(.ant-divider-inner-text) {
      padding: 0 12px;
    }

    ::v-deep(.ant-tag) {
      border-radius: 6px;
      font-weight: 500;
    }

    ::v-deep(.ant-badge-count) {
      font-weight: 600;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
  }
}

// 반응형 디자인
@media (max-width: 768px) {
  .certificate-work-order-list {
    padding: 15px;

    .page-header {
      padding: 20px;
      margin-bottom: 20px;

      .page-title {
        font-size: 22px;

        i {
          font-size: 20px;
        }
      }
    }

    .content-container {
      padding: 20px;

      .filter-section {
        flex-direction: column;
        align-items: stretch;
        gap: 16px;

        .filter-right {
          flex-direction: column;
        }
      }
    }
  }

  .detail-drawer-content {
    .detail-section {
      .detail-grid {
        grid-template-columns: 1fr;
      }
    }
  }
}
</style>